import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconRefresh, IconX } from "@tabler/icons-react";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import {
  VERSION_CHECK_ENABLED,
  fetchDeployedBuildId,
  isNewerBuild,
} from "@/lib/version/build-id";

/** How often an open tab asks whether a newer build is deployed. */
export const VERSION_POLL_MS = 5 * 60_000;

/**
 * Offers a reload when a newer build is deployed (lib/version/build-id.ts).
 * Mounted once at the root. Polls every five minutes and whenever the tab
 * regains focus; never reloads by itself. Dismissing hides it until a
 * different build id shows up.
 *
 * Its own card rather than PBanner: PBanner's root is a size container
 * (`@container`), so inside a shrink-to-fit fixed host it collapses to its
 * border (measured: 2px), and it hides the description below 32rem — the
 * one sentence that says to save first.
 *
 * Bottom-CENTRE: the bottom-right corner already holds the background-jobs
 * panel and the translate controller (both z-[300]+, both shifted by
 * --ai-rail-push), toasts sit top-centre. The centre is shifted by half the
 * docked AI rail so the card stays centred over the visible app.
 *
 * The live region is mounted always and only its content changes, so screen
 * readers announce the notice (a region mounted together with its message is
 * usually not announced — see Toast.tsx). `aria-live` WITHOUT `role="status"`:
 * e2e specs query `getByRole("status")` unscoped and assume one per page
 * (connection-dot-offline.spec.ts), so this region must not match it.
 */
export function NewVersionNotice({
  enabled = VERSION_CHECK_ENABLED,
  fetchId = fetchDeployedBuildId,
}: {
  enabled?: boolean;
  fetchId?: () => Promise<string | null>;
}) {
  const t = useTranslations("appUpdate");
  const tBanner = useTranslations("banner");
  const [deployed, setDeployed] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const id = await fetchId();
      if (id !== null) setDeployed(id);
    } finally {
      inFlight.current = false;
    }
  }, [fetchId]);

  useEffect(() => {
    if (!enabled) return;
    const timer = window.setInterval(check, VERSION_POLL_MS);
    const onFocus = () => void check();
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, check]);

  if (!enabled) return null;
  const visible = isNewerBuild(deployed) && dismissed !== deployed;

  return (
    <div aria-live="polite" aria-atomic="true" data-testid="new-version-live-region">
      {visible && (
        <div
          data-testid="new-version-notice"
          className="fixed bottom-4 z-[310] w-[min(26rem,calc(100vw-var(--ai-rail-push)-2rem))] -translate-x-1/2 left-1/2 sm:left-[calc((100vw-var(--ai-rail-push))/2)] surface-primary border outline-quaternary rounded-xl shadow-lg p-4 flex items-start gap-3"
        >
          <div className="flex-1 min-w-0">
            <p className="body-2-medium font-primary">{t("title")}</p>
            <p className="body-3-regular font-secondary mt-1">{t("description")}</p>
            <div className="mt-3">
              <PrimaryButton
                text={t("reload")}
                icon={IconRefresh}
                size="xsmall"
                width="w-max"
                onClick={() => window.location.reload()}
              />
            </div>
          </div>
          <button
            type="button"
            aria-label={tBanner("dismiss")}
            onClick={() => setDismissed(deployed)}
            className="shrink-0 rounded-md p-1 font-tertiary hover:surface-secondary focus-ring"
          >
            <IconX size={16} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
